import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BatteryCharging,
  Battery,
  BatteryLow,
  Zap,
  Car,
  Search,
  AlertTriangle,
} from 'lucide-react';
import { evApi, type EvDeviceSnapshot } from '../services/api';
import { useHasModule } from '../store/modules';

function socColor(soc: number | null): { bg: string; text: string; bar: string } {
  if (soc === null) return { bg: 'bg-gray-100', text: 'text-gray-500', bar: 'bg-gray-300' };
  if (soc <= 20) return { bg: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-500' };
  if (soc <= 40) return { bg: 'bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-500' };
  if (soc <= 70) return { bg: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-500' };
  return { bg: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-600' };
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "il y a moins d'une minute";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

export default function EvDashboardPage() {
  const { t } = useTranslation();
  const hasEvModule = useHasModule('EV');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<'name' | 'soc' | 'range'>('name');

  const { data, isLoading, error } = useQuery({
    queryKey: ['ev-fleet'],
    queryFn: () => evApi.getFleet(),
    enabled: hasEvModule,
    refetchInterval: 60_000,
  });

  const filteredSorted = useMemo(() => {
    const list = data?.devices ?? [];
    const filtered = search.trim()
      ? list.filter((d) => {
          const q = search.toLowerCase();
          return (
            d.name.toLowerCase().includes(q) ||
            (d.plate ?? '').toLowerCase().includes(q) ||
            `${d.evModel?.make ?? ''} ${d.evModel?.model ?? ''}`.toLowerCase().includes(q)
          );
        })
      : list;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'soc') {
        return (a.currentSoc ?? -1) - (b.currentSoc ?? -1);
      }
      if (sortKey === 'range') {
        return (a.estimatedRangeKm ?? -1) - (b.estimatedRangeKm ?? -1);
      }
      return a.name.localeCompare(b.name);
    });
  }, [data?.devices, search, sortKey]);

  if (!hasEvModule) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-800">
          <h2 className="font-semibold mb-1">{t('ev.module_off_title')}</h2>
          <p className="text-sm">
            {t('ev.module_off_body')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BatteryCharging className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-600" />
          {t('ev.title')}
        </h1>
        <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
          {t('ev.subtitle')}
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {t('ev.load_error')}
        </div>
      )}

      {/* KPI cards — 2 cols compact on mobile, 5 cols on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-6">
        <KpiCard
          label={t('ev.kpi_vehicles')}
          longLabel={t('ev.kpi_vehicles_long')}
          value={data?.summary.totalEvDevices ?? '—'}
          icon={<Car className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />}
          loading={isLoading}
        />
        <KpiCard
          label={t('ev.kpi_charging')}
          value={data?.summary.currentlyCharging ?? '—'}
          icon={<Zap className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />}
          loading={isLoading}
        />
        <KpiCard
          label={t('ev.kpi_avg_level')}
          longLabel={t('ev.kpi_avg_level_long')}
          value={
            data?.summary.avgSoc !== null && data?.summary.avgSoc !== undefined
              ? `${data.summary.avgSoc}%`
              : '—'
          }
          icon={<Battery className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />}
          loading={isLoading}
        />
        <KpiCard
          label={t('ev.kpi_low_short')}
          longLabel={t('ev.kpi_low_long')}
          value={data?.summary.lowSocCount ?? '—'}
          icon={<BatteryLow className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />}
          loading={isLoading}
          highlight={data?.summary.lowSocCount ? data.summary.lowSocCount > 0 : false}
        />
        <KpiCard
          label={t('ev.kpi_range')}
          longLabel={t('ev.kpi_range_long')}
          value={
            data?.summary.totalEstimatedRangeKm !== null &&
            data?.summary.totalEstimatedRangeKm !== undefined
              ? `${data.summary.totalEstimatedRangeKm} km`
              : '—'
          }
          icon={<BatteryCharging className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />}
          loading={isLoading}
        />
      </div>

      {/* Toolbar — search + sort wraps to 2 lines on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('ev.search_placeholder')}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as 'name' | 'soc' | 'range')}
            className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="name">{t('ev.sort_by_name')}</option>
            <option value="soc">{t('ev.sort_by_soc')}</option>
            <option value="range">{t('ev.sort_by_range')}</option>
          </select>
          <span className="text-xs sm:text-sm text-gray-500 shrink-0">
            {t('ev.vehicles_count_short', { count: filteredSorted.length })}
          </span>
        </div>
      </div>

      {/* Empty / loading states */}
      {isLoading && !data ? (
        <div className="p-8 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
          {t('ev.loading')}
        </div>
      ) : filteredSorted.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500 bg-white rounded-lg border border-gray-200">
          {t('ev.no_ev_vehicles')}
        </div>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="space-y-2 sm:hidden">
            {filteredSorted.map((d) => (
              <DeviceCard key={d.deviceId} d={d} />
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden sm:block bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">{t('ev.th_vehicle')}</th>
                  <th className="px-4 py-2 text-left">{t('ev.th_ev_model')}</th>
                  <th className="px-4 py-2 text-left">{t('ev.th_battery_level')}</th>
                  <th className="px-4 py-2 text-right">{t('ev.th_estimated_range')}</th>
                  <th className="px-4 py-2 text-center">{t('ev.th_state')}</th>
                  <th className="px-4 py-2 text-right">{t('ev.th_last_reading')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredSorted.map((d) => (
                  <DeviceRow key={d.deviceId} d={d} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({
  label,
  longLabel,
  value,
  icon,
  loading,
  highlight,
}: {
  label: string;
  longLabel?: string;
  value: string | number;
  icon: React.ReactNode;
  loading?: boolean;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border ${
        highlight ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
      } px-3 py-2 sm:px-4 sm:py-3`}
    >
      <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">
        {icon}
        <span className="sm:hidden">{label}</span>
        <span className="hidden sm:inline">{longLabel ?? label}</span>
      </div>
      <div
        className={`mt-0.5 sm:mt-1 text-xl sm:text-2xl font-bold ${
          highlight ? 'text-red-700' : 'text-gray-900'
        }`}
      >
        {loading ? '…' : value}
      </div>
    </div>
  );
}

function DeviceCard({ d }: { d: EvDeviceSnapshot }) {
  const colors = socColor(d.currentSoc);
  return (
    <Link
      to={`/devices?focus=${d.deviceId}`}
      className="block bg-white rounded-lg border border-gray-200 p-3 active:bg-gray-50"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-gray-900 truncate">{d.name}</div>
          <div className="text-xs text-gray-500 truncate mt-0.5">
            {d.plate ? <span>{d.plate}</span> : null}
            {d.plate && d.evModel ? <span className="mx-1.5">·</span> : null}
            {d.evModel ? (
              <span>
                {d.evModel.make} {d.evModel.model}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <AlertTriangle className="h-3 w-3" /> modèle non défini
              </span>
            )}
          </div>
        </div>
        {d.currentChargingState === 1 && (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
            <Zap className="h-3 w-3" /> charge
          </span>
        )}
      </div>

      {/* Battery level line */}
      <div className="mt-2 flex items-center gap-3">
        <div className="flex-1">
          <div className="h-2 w-full bg-gray-100 rounded">
            <div
              className={`h-2 rounded ${colors.bar} transition-all`}
              style={{ width: `${d.currentSoc ?? 0}%` }}
            />
          </div>
        </div>
        <div className={`text-sm font-semibold tabular-nums ${colors.text} w-12 text-right`}>
          {d.currentSoc !== null ? `${d.currentSoc}%` : '—'}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span className="tabular-nums">
          {d.estimatedRangeKm !== null ? `≈ ${d.estimatedRangeKm} km autonomie` : '—'}
        </span>
        <span>{relativeTime(d.socObservedAt)}</span>
      </div>
    </Link>
  );
}

function DeviceRow({ d }: { d: EvDeviceSnapshot }) {
  const colors = socColor(d.currentSoc);
  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-2">
        <Link
          to={`/devices?focus=${d.deviceId}`}
          className="font-medium text-gray-900 hover:text-emerald-700"
        >
          {d.name}
        </Link>
        {d.plate && <span className="ml-2 text-xs text-gray-500">{d.plate}</span>}
      </td>
      <td className="px-4 py-2 text-gray-700">
        {d.evModel ? (
          <>
            {d.evModel.make} {d.evModel.model}
            {d.evModel.variant ? ` ${d.evModel.variant}` : ''}
            <div className="text-xs text-gray-400">
              {d.evModel.batteryKwh} kWh · {d.evModel.rangeWltpKm} km WLTP
            </div>
          </>
        ) : (
          <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
            <AlertTriangle className="h-3 w-3" /> Modèle non défini
          </span>
        )}
      </td>
      <td className="px-4 py-2 w-44">
        <div className={`text-xs font-semibold ${colors.text}`}>
          {d.currentSoc !== null ? `${d.currentSoc}%` : '—'}
        </div>
        <div className="mt-1 h-2 w-32 bg-gray-100 rounded overflow-hidden">
          <div
            className={`h-2 ${colors.bar} transition-all`}
            style={{ width: `${d.currentSoc ?? 0}%` }}
          />
        </div>
      </td>
      <td className="px-4 py-2 text-right tabular-nums">
        {d.estimatedRangeKm !== null ? `${d.estimatedRangeKm} km` : '—'}
      </td>
      <td className="px-4 py-2 text-center">
        {d.currentChargingState === 1 ? (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
            <Zap className="h-3 w-3" /> En charge
          </span>
        ) : d.currentChargingState === 0 ? (
          <span className="text-xs text-gray-500">Au repos</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-right text-xs text-gray-500">
        {relativeTime(d.socObservedAt)}
      </td>
    </tr>
  );
}
